/**
 * App.tsx has all providers and routed components declared
 *
 * @remarks
 * Design system for this project is based on Chakra and the design resides on Figma
 */

/**
 * SASS
 * @remarks
 * Please refrain from using module based css @example "app.module.css"
 * Coupling components with module based css files will cause weird behaviour when SCSS is present.
 * All SCSS files are imported into main.scss file. Please refrain from importing different scss files into different components.
 */

/**
 * Linting
 * @remarks
 * The configuration is to set to strictly follow TypeScript and ESLint conventions
 * Do not modify this configuration unless you understand the implications
 */

import { ChakraProvider } from "@chakra-ui/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Backdrop } from "components";
import Footer from "components/base/molecules/Footer";
import _ from "lodash";
import { Suspense, useEffect } from "react";
import { Route, Switch, useLocation } from "react-router-dom";
import { getPageTitles } from "utils";
import Header from "./components/base/molecules/Header";
import { ProtectedRoute } from "./components/helpers/ProtectedRoute";
import routes from "./config/Routes";
import Theme from "./config/Theme";
import { AuthContextProvider } from "./context/AuthContext";
import { ModalProvider } from "./context/ModalContext";
import "./scss/main.scss";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      cacheTime: 1000 * 60 * 7,
      enabled: false,
      staleTime: Infinity,
      retry: 1,
    },
  },
});

export const App: () => JSX.Element = () => {
  const location = useLocation();

  const isJobsPage = location?.pathname?.includes("/jobs");
  const isSearchJobPage = location?.pathname === "/jobs/search";
  useEffect(() => {
    if (location !== undefined) {
      const title = getPageTitles(location?.pathname);
      document.title = title;
    }
  }, [location?.pathname]);

  return (
    <QueryClientProvider client={queryClient}>
      <ChakraProvider theme={Theme}>
        <AuthContextProvider>
          {/* <BrowserRouter> */}
          <Backdrop display={!isSearchJobPage} />
          <ModalProvider>
            <Header />
            <div
              className={`grow ${
                location.pathname.includes("survey")
                  ? "bg-[#f3f3f3]"
                  : "my-6 bg-[initial]"
              }`}
            >
              <Switch>
                {routes.map((route) => {
                  return (
                    <Route
                      key={_.uniqueId("hjs_route_")}
                      path={route.path}
                      exact
                    >
                      <Suspense fallback={null}>
                        {route.protected ? (
                          <ProtectedRoute>
                            <route.component />
                          </ProtectedRoute>
                        ) : (
                          <route.component />
                        )}
                      </Suspense>
                    </Route>
                  );
                })}
              </Switch>
            </div>
          </ModalProvider>
          {!isJobsPage && <Footer />}
          {/* </BrowserRouter> */}
        </AuthContextProvider>
      </ChakraProvider>
    </QueryClientProvider>
  );
};
